import {
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Popover,
  Text,
  useMediaQuery,
} from '@maas-components/core'
import { Assignment, CloseOutlined, ExitToAppOutlined } from '@maas-components/icons'
import { Theme } from '@maas-components/styles'
import PopupState, { bindPopover } from 'material-ui-popup-state'
import { Trans } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { ERoute } from 'common/entities/routes'
import { ENQUEUE_SNACKBAR_ANCHOR_ORIGIN } from '../../common/constants/utils'
import ProfileButton from './ProfileButton'
import useStyles from './style'
import UserInfo from './UserInfo'

interface IProfileProps {
  name: string
  email: string
  onLogout?: () => void
  enableUserInfo?: boolean
  description: string
}

export default function AppProfile({
  name,
  email,
  onLogout,
  enableUserInfo,
  description,
}: IProfileProps) {
  const classes = useStyles()
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('xs'))
  const history = useHistory()

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleLogout = (popupState: any) => {
    popupState.close()
    sessionStorage.removeItem('maas-tenantId')
    if (onLogout) {
      onLogout()
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const goToAcceptanceTerm = (popupState: any) => {
    popupState.close()
    history.push(ERoute.ACCEPTANCE_TERM)
  }

  return (
    <PopupState variant="popover">
      {popupState => (
        <>
          <ProfileButton
            data-testid="app-profile-button"
            enableUserInfo={enableUserInfo}
            name={name}
            popupState={popupState}
          />
          <Popover
            className={`${classes.paper} ${isMobile ? classes.paperMobile : ''}`}
            {...bindPopover(popupState)}
            anchorReference={isMobile ? 'anchorPosition' : 'anchorEl'}
            anchorPosition={{ left: 0, top: 0 }}
            marginThreshold={isMobile ? 0 : 16}
            anchorOrigin={ENQUEUE_SNACKBAR_ANCHOR_ORIGIN}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
          >
            <Box className={`${classes.root} ${classes.paper}`}>
              {isMobile && (
                <IconButton
                  data-testid="close-button-test-id"
                  className={classes.closeButton}
                  onClick={() => {
                    popupState.setOpen(false)
                  }}
                >
                  <CloseOutlined />
                </IconButton>
              )}
              <List>
                <Box>
                  <UserInfo name={name} email={email} description={description} />
                </Box>

                <Divider />

                <Box>
                  <ListItem
                    button
                    data-testid="acceptance-term"
                    className={classes.iconListItem}
                    onClick={() => goToAcceptanceTerm(popupState)}
                  >
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center">
                          <Assignment />
                          <Text component="span" fontWeight="bold">
                            <Trans>app.profile.acceptanceTerm</Trans>
                          </Text>
                        </Box>
                      }
                    />
                  </ListItem>
                </Box>

                <Divider />

                <Box>
                  <ListItem
                    button
                    data-testid="logout"
                    className={classes.iconListItem}
                    onClick={() => handleLogout(popupState)}
                  >
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center">
                          <ExitToAppOutlined />
                          <Text component="span" fontWeight="bold">
                            <Trans>app.profile.logout</Trans>
                          </Text>
                        </Box>
                      }
                    />
                  </ListItem>
                </Box>
              </List>
            </Box>
          </Popover>
        </>
      )}
    </PopupState>
  )
}
